import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { AgentAvatar } from "../agent-message-text";
import { GidsSidekick } from "./gids-sidekick";
import {
  APPLICATION_REF,
  INTAKE_DOCUMENTS,
  type IntakeField,
  type IntakeStep,
  PRESCREEN_FLAGS,
  type PrescreenFlag,
  resolveTip,
  STEPS,
  type Tip,
  tipVisuals,
} from "./intake-data";

type Phase = "upload" | "analyzing" | "wizard";

export function NieuweAanvraagPage() {
  const navigate = useNavigate();

  // Phase 0 gate → AI analysis → guided wizard.
  const [phase, setPhase] = useState<Phase>("upload");
  const [uploadedDocs, setUploadedDocs] = useState<Record<string, string>>({});

  const [current, setCurrent] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  const [checking, setChecking] = useState(false);
  const [checkDone, setCheckDone] = useState(false);
  const [signature, setSignature] = useState<{ name: string; signedAt: string } | null>(null);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  function confirmSubmit() {
    setShowSubmitDialog(false);
    setCurrent(STEPS.length - 1);
    toast("Dossier ingediend bij het secretariaat", {
      description: `${APPLICATION_REF} staat klaar voor de volledigheidscheck. Je ontvangt een e-mail zodra er nieuws is.`,
    });
    void navigate("/dashboard/mijn-aanvragen");
  }

  // Track live field values for adaptive branching
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      STEPS.flatMap((s) =>
        s.fields.flatMap((f) => {
          const entries: [string, string][] = [[f.id, f.value]];
          // Radio fields carry a pre-filled toelichting for their default answer,
          // seeded per-answer under a derived key so it never collides with real
          // fields and so each answer remembers its own text when you switch back.
          const prefill = f.type === "radio" ? f.toelichting?.[f.value] : undefined;
          if (prefill !== undefined) {
            entries.push([`${f.id}::toelichting::${f.value}`, prefill]);
          }
          return entries;
        }),
      ),
    ),
  );
  // Track which AI-prefilled fields the user has explicitly confirmed
  const [confirmedFields, setConfirmedFields] = useState<Set<string>>(new Set());
  // Field the rail's tip-overview last pointed at, briefly highlighted so the
  // link between a guide tip and its input is unmistakable.
  const [highlightedField, setHighlightedField] = useState<string | null>(null);
  const highlightTimer = useRef<number | null>(null);

  useEffect(() => () => window.clearTimeout(highlightTimer.current ?? undefined), []);

  function focusField(fieldId: string) {
    document
      .getElementById(`field-${fieldId}`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedField(fieldId);
    window.clearTimeout(highlightTimer.current ?? undefined);
    highlightTimer.current = window.setTimeout(() => setHighlightedField(null), 1900);
  }

  const step = STEPS[current] ?? STEPS[0];
  const isLast = current === STEPS.length - 1;

  // AI "analyzing" pause before the wizard opens, only the act of feeding it
  // documents triggers this; the AI never runs on an empty dossier.
  useEffect(() => {
    if (phase !== "analyzing") {
      return;
    }
    const t = setTimeout(() => {
      setPhase("wizard");
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 2400);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (phase !== "wizard" || !isLast || checkDone) {
      return;
    }
    setChecking(true);
    const t = setTimeout(() => {
      setChecking(false);
      setCheckDone(true);
    }, 1900);
    return () => clearTimeout(t);
  }, [phase, isLast, checkDone]);

  function goTo(index: number) {
    const clamped = Math.max(0, Math.min(STEPS.length - 1, index));
    setCurrent(clamped);
    setMaxReached((m) => Math.max(m, clamped));
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function goToDocuments() {
    setPhase("upload");
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function handleFieldChange(id: string, value: string) {
    setFieldValues((prev) => ({ ...prev, [id]: value }));
  }

  function handleFieldConfirm(id: string) {
    setConfirmedFields((prev) => new Set(prev).add(id));
  }

  function handleUpload(docId: string, filename: string) {
    setUploadedDocs((prev) => ({ ...prev, [docId]: filename }));
  }

  function handleRemoveDoc(docId: string) {
    setUploadedDocs((prev) => {
      const next = { ...prev };
      delete next[docId];
      return next;
    });
  }

  function loadExampleDocs() {
    const seeded: Record<string, string> = {};
    for (const doc of INTAKE_DOCUMENTS) {
      if (doc.exampleFile) {
        seeded[doc.id] = doc.exampleFile;
      }
    }
    setUploadedDocs(seeded);
  }

  if (!step) {
    return null;
  }

  const requiredMet = INTAKE_DOCUMENTS.filter((d) => d.required).every((d) => uploadedDocs[d.id]);
  const uploadedList = INTAKE_DOCUMENTS.filter((d) => uploadedDocs[d.id]).map((d) => ({
    label: d.label,
    name: uploadedDocs[d.id] ?? "",
    required: d.required,
  }));

  // Adaptive branching: hide fields whose showIf condition isn't met
  const visibleFields = step.fields.filter(
    (f) => !f.showIf || fieldValues[f.showIf.fieldId] === f.showIf.value,
  );

  const allAiFields = STEPS.flatMap((s) => s.fields).filter((f) => f.aiPrefilled);
  const aiFieldsInStep = step.fields.filter((f) => f.aiPrefilled);
  const confirmedInStep = aiFieldsInStep.filter((f) => confirmedFields.has(f.id)).length;

  return (
    <section ref={topRef}>
      <style>{INTAKE_KEYFRAMES}</style>

      {/* Page header */}
      <div className="page-head">
        <div>
          <p className="eyebrow">Nieuwe ethische aanvraag · begeleide intake</p>
          <h1>Bouw je EA SHW-dossier stap voor stap op</h1>
          <p className="ua-muted" style={{ marginTop: "8px", maxWidth: "680px", fontSize: "16px" }}>
            De ethische gids leidt je door de aanvraag, vult velden alvast in op basis van je
            documenten en toont onderweg relevante tips. Alles op deze pagina is fictieve
            voorbeelddata.
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <span
            style={{
              background: "var(--ua-bg)",
              border: "1px solid var(--ua-border)",
              borderRadius: "999px",
              padding: "6px 14px",
              fontSize: "12px",
              fontWeight: 700,
              whiteSpace: "nowrap",
              color: "var(--ua-muted)",
              alignSelf: "center",
            }}
          >
            {APPLICATION_REF} · concept
          </span>
          <button
            className="btn btn--ghost"
            onClick={() => void navigate("/dashboard/mijn-aanvragen")}
          >
            Bewaar concept
          </button>
        </div>
      </div>

      {/* Stepper: the "Documenten" pseudo-step precedes the 6 wizard steps */}
      <div className="card" style={{ padding: "20px 24px", marginBottom: "var(--ua-space)" }}>
        <div className="stepper">
          <button
            className={`step ${phase === "wizard" ? "step--done" : "step--active"}`}
            onClick={() => phase === "wizard" && goToDocuments()}
            disabled={phase !== "wizard"}
            style={{
              background: "none",
              border: "none",
              cursor: phase === "wizard" ? "pointer" : "default",
              font: "inherit",
            }}
          >
            <span className="step__dot">{phase === "wizard" ? "✓" : 1}</span>
            <span className="step__label">Documenten</span>
          </button>
          {STEPS.map((s, i) => {
            const inWizard = phase === "wizard";
            const state = !inWizard
              ? ""
              : i < current
                ? "step--done"
                : i === current
                  ? "step--active"
                  : "";
            const clickable = inWizard && i <= maxReached;
            return (
              <button
                key={s.id}
                className={`step ${state}`}
                onClick={() => clickable && goTo(i)}
                disabled={!clickable}
                style={{
                  background: "none",
                  border: "none",
                  cursor: clickable ? "pointer" : "default",
                  font: "inherit",
                }}
              >
                <span className="step__dot">{inWizard && i < current ? "✓" : i + 2}</span>
                <span className="step__label">{s.navLabel}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Two-column body: form + assistant rail */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 320px",
          gap: "var(--ua-space)",
          alignItems: "start",
        }}
      >
        {/* Main column */}
        <article className="card" style={{ overflow: "hidden" }}>
          {phase === "upload" ? (
            <OnboardingUpload
              uploadedDocs={uploadedDocs}
              requiredMet={requiredMet}
              onUpload={handleUpload}
              onRemove={handleRemoveDoc}
              onLoadExamples={loadExampleDocs}
              onStart={() => setPhase("analyzing")}
            />
          ) : phase === "analyzing" ? (
            <AnalyzingState docs={uploadedList} />
          ) : (
            <>
              <div className="card__head" style={{ alignItems: "flex-start" }}>
                <div>
                  <p className="eyebrow" style={{ marginBottom: "4px" }}>
                    {step.eyebrow}
                  </p>
                  <h2 style={{ fontSize: "20px" }}>{step.title}</h2>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: "6px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "var(--ua-muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {step.luik}
                  </span>
                  {/* AI confirmation progress badge */}
                  {aiFieldsInStep.length > 0 && (
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 700,
                        color:
                          confirmedInStep === aiFieldsInStep.length
                            ? "var(--ua-success)"
                            : "var(--ua-blue)",
                        background:
                          confirmedInStep === aiFieldsInStep.length ? "#edfaf2" : "#eef2f9",
                        border: `1px solid ${confirmedInStep === aiFieldsInStep.length ? "#a3e4c1" : "#c5d0e8"}`,
                        borderRadius: "999px",
                        padding: "2px 9px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      ✦ {confirmedInStep}/{aiFieldsInStep.length} AI-velden bevestigd
                    </span>
                  )}
                </div>
              </div>

              <div style={{ padding: "20px" }}>
                <p
                  style={{
                    color: "var(--ua-muted)",
                    fontSize: "14px",
                    margin: "0 0 20px",
                    maxWidth: "640px",
                    lineHeight: 1.6,
                  }}
                >
                  {step.intro}
                </p>

                {isLast ? (
                  <ReviewStep
                    checking={checking}
                    checkDone={checkDone}
                    onJump={goTo}
                    documents={uploadedList}
                    fieldValues={fieldValues}
                    confirmedCount={confirmedFields.size}
                    totalAiFields={allAiFields.length}
                    docsUploaded={{
                      methodologie: Boolean(uploadedDocs.methodologie),
                      consent: Boolean(uploadedDocs.consent),
                    }}
                    signature={signature}
                    onSign={(name) =>
                      setSignature({
                        name,
                        signedAt: new Date().toLocaleString("nl-BE", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        }),
                      })
                    }
                    onUnsign={() => setSignature(null)}
                  />
                ) : (
                  <div key={step.id} style={{ display: "grid", gap: "18px" }}>
                    {visibleFields.map((f) => (
                      <FieldRow
                        key={f.id}
                        field={f}
                        tips={step.tips.filter((t) => t.fieldId === f.id)}
                        highlighted={highlightedField === f.id}
                        currentValue={fieldValues[f.id] ?? f.value}
                        toelichtingValue={
                          fieldValues[`${f.id}::toelichting::${fieldValues[f.id] ?? f.value}`] ?? ""
                        }
                        confirmed={confirmedFields.has(f.id)}
                        onFieldChange={handleFieldChange}
                        onConfirm={handleFieldConfirm}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Step navigation */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "16px",
                  padding: "16px 20px",
                  borderTop: "1px solid var(--ua-border)",
                  background: "var(--ua-bg)",
                }}
              >
                <button
                  className="btn btn--ghost"
                  onClick={() => (current === 0 ? goToDocuments() : goTo(current - 1))}
                >
                  {current === 0 ? "← Documenten" : "← Vorige"}
                </button>

                {isLast ? (
                  <button
                    className="btn btn--primary"
                    disabled={!checkDone || !signature}
                    onClick={() => setShowSubmitDialog(true)}
                  >
                    Indienen bij secretariaat
                  </button>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    {aiFieldsInStep.length > 0 && confirmedInStep < aiFieldsInStep.length && (
                      <AiConfirmProgress
                        confirmed={confirmedInStep}
                        total={aiFieldsInStep.length}
                      />
                    )}
                    <button
                      className="btn btn--primary"
                      disabled={confirmedInStep < aiFieldsInStep.length}
                      onClick={() => goTo(current + 1)}
                    >
                      {current === STEPS.length - 2 ? "Naar voorafcheck →" : "Volgende →"}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </article>

        {/* The guide sidekick, one living panel across every phase */}
        <GidsSidekick
          phase={phase}
          step={current}
          confirmedCount={confirmedFields.size}
          totalAiFields={allAiFields.length}
          docsUploaded={{
            methodologie: Boolean(uploadedDocs.methodologie),
            consent: Boolean(uploadedDocs.consent),
          }}
          fieldValues={fieldValues}
          onFocusField={focusField}
          reviewChecking={checking}
        />
      </div>

      <SubmitConfirmDialog
        open={showSubmitDialog}
        onOpenChange={setShowSubmitDialog}
        onConfirm={confirmSubmit}
      />
    </section>
  );
}

/* ── Final confirmation before the dossier leaves the researcher's hands ──── */

function SubmitConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ color: "var(--ua-navy)" }}>
            Voor je indient bij het secretariaat
          </DialogTitle>
          <DialogDescription>
            Dit dossier gaat na bevestiging naar het secretariaat. Je kan het daarna niet meer zelf
            aanpassen.
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: "grid", gap: "14px", fontSize: "13px", lineHeight: 1.6 }}>
          <div
            style={{
              border: "1px solid var(--ua-border)",
              borderRadius: "6px",
              padding: "12px 14px",
            }}
          >
            <div style={{ fontWeight: 700, color: "var(--ua-navy)", marginBottom: "4px" }}>
              Verwachte doorlooptijd
            </div>
            <div style={{ color: "var(--ua-muted)" }}>
              Gemiddeld <strong>10 werkdagen</strong> tot een eerste advies, vanaf het moment dat
              het secretariaat je dossier volledig verklaart.
            </div>
          </div>

          <div
            style={{
              border: "1px solid var(--ua-border)",
              borderRadius: "6px",
              padding: "12px 14px",
            }}
          >
            <div style={{ fontWeight: 700, color: "var(--ua-navy)", marginBottom: "4px" }}>
              Bespreking op het bureau
            </div>
            <div style={{ color: "var(--ua-muted)" }}>
              Als je dossier op tijd volledig is, staat het op de agenda van{" "}
              <strong>woensdag 5 augustus 2026</strong>. Is het dan nog niet klaar, dan schuift het
              door naar de eerstvolgende vergadering.
            </div>
          </div>

          <div
            style={{
              border: "1px solid var(--ua-border)",
              borderRadius: "6px",
              padding: "12px 14px",
            }}
          >
            <div style={{ fontWeight: 700, color: "var(--ua-navy)", marginBottom: "4px" }}>
              Hoe je op de hoogte blijft
            </div>
            <ul style={{ margin: 0, paddingLeft: "18px", color: "var(--ua-muted)" }}>
              <li>Een e-mail zodra het secretariaat je dossier volledig heeft bevonden</li>
              <li>Een e-mail zodra het dossier op de bureau-agenda wordt geplaatst</li>
              <li>Een e-mail met het advies zodra het bureau heeft vergaderd</li>
              <li>
                De actuele status is altijd te volgen via <strong>Mijn aanvragen</strong>
              </li>
            </ul>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginTop: "6px",
          }}
        >
          <button className="btn btn--ghost" onClick={() => onOpenChange(false)}>
            Nog niet, terug naar dossier
          </button>
          <button className="btn btn--primary" onClick={onConfirm}>
            Bevestig en dien in
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Phase 0 · onboarding upload gate ─────────────────────────────────────── */

function OnboardingUpload({
  uploadedDocs,
  requiredMet,
  onUpload,
  onRemove,
  onLoadExamples,
  onStart,
}: {
  uploadedDocs: Record<string, string>;
  requiredMet: boolean;
  onUpload: (docId: string, filename: string) => void;
  onRemove: (docId: string) => void;
  onLoadExamples: () => void;
  onStart: () => void;
}) {
  const [dragOver, setDragOver] = useState(false);

  // Drop or pick → fill the next empty document slots in order (required first).
  function fillSlots(names: string[]) {
    const empties = INTAKE_DOCUMENTS.filter((d) => !uploadedDocs[d.id]).sort(
      (a, b) => Number(b.required) - Number(a.required),
    );
    names.forEach((name, i) => {
      const slot = empties[i];
      if (slot) {
        onUpload(slot.id, name);
      }
    });
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const names = Array.from(e.dataTransfer.files).map((f) => f.name);
    if (names.length > 0) {
      fillSlots(names);
    }
  }

  const requiredCount = INTAKE_DOCUMENTS.filter((d) => d.required).length;
  const requiredUploaded = INTAKE_DOCUMENTS.filter((d) => d.required && uploadedDocs[d.id]).length;

  return (
    <>
      <div className="card__head" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow" style={{ marginBottom: "4px" }}>
            Stap 0 · Documenten toevoegen
          </p>
          <h2 style={{ fontSize: "20px" }}>Voeg eerst je documenten toe</h2>
        </div>
        <span
          style={{
            fontSize: "11px",
            fontWeight: 700,
            color: "var(--ua-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            whiteSpace: "nowrap",
          }}
        >
          {requiredUploaded}/{requiredCount} verplicht
        </span>
      </div>

      <div style={{ padding: "20px" }}>
        <p
          style={{
            color: "var(--ua-muted)",
            fontSize: "14px",
            margin: "0 0 18px",
            maxWidth: "640px",
            lineHeight: 1.6,
          }}
        >
          Etna vult je aanvraag pas in op basis van wat je hier toevoegt. Ze leest niets op
          voorhand. Hoe meer je toevoegt, hoe nauwkeuriger Etna je dossier kan voorbereiden. Niets
          wordt ingediend; alles blijft een concept.
        </p>

        {/* Dropzone */}
        <label
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          style={{
            display: "block",
            border: `2px dashed ${dragOver ? "var(--ua-navy)" : "var(--ua-border)"}`,
            borderRadius: "8px",
            background: dragOver ? "#eef2f9" : "var(--ua-surface)",
            padding: "26px 20px",
            textAlign: "center",
            cursor: "pointer",
            transition: "border-color 0.15s ease, background 0.15s ease",
          }}
        >
          <input
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              const names = Array.from(e.target.files ?? []).map((f) => f.name);
              if (names.length > 0) {
                fillSlots(names);
              }
              e.target.value = "";
            }}
          />
          <span style={{ fontSize: "22px", display: "block", marginBottom: "6px" }}>📎</span>
          <span
            style={{ fontWeight: 700, fontSize: "14px", color: "var(--ua-navy)", display: "block" }}
          >
            Sleep bestanden hierheen of klik om te bladeren
          </span>
          <span
            style={{
              fontSize: "12px",
              color: "var(--ua-muted)",
              display: "block",
              marginTop: "2px",
            }}
          >
            PDF of Word · bestanden worden aan de juiste vakken hieronder gekoppeld
          </span>
        </label>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "8px",
            margin: "12px 0 18px",
          }}
        >
          <span style={{ fontSize: "12px", color: "var(--ua-muted)" }}>
            Geen bestanden bij de hand?
          </span>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onLoadExamples}
            style={{ fontSize: "12px", padding: "6px 12px" }}
          >
            Gebruik de voorbeelddocumenten van deze demo
          </button>
        </div>

        {/* Recommended-documents checklist */}
        <p className="section-kicker" style={{ marginBottom: "10px" }}>
          Aanbevolen documenten
        </p>
        <div style={{ display: "grid", gap: "10px" }}>
          {INTAKE_DOCUMENTS.map((doc) => (
            <DocumentRow
              key={doc.id}
              docId={doc.id}
              label={doc.label}
              required={doc.required}
              why={doc.why}
              hint={doc.hint}
              filename={uploadedDocs[doc.id]}
              onUpload={onUpload}
              onRemove={onRemove}
            />
          ))}
        </div>
      </div>

      {/* Gate footer */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          padding: "16px 20px",
          borderTop: "1px solid var(--ua-border)",
          background: "var(--ua-bg)",
        }}
      >
        <span style={{ fontSize: "12px", color: "var(--ua-muted)", maxWidth: "360px" }}>
          {requiredMet
            ? "Klaar. Etna leest je documenten en vult de aanvraag alvast in."
            : "Voeg de twee verplichte documenten toe om de AI-analyse te starten."}
        </span>
        <button className="btn btn--primary" disabled={!requiredMet} onClick={onStart}>
          Start aanvraag →
        </button>
      </div>
    </>
  );
}

function DocumentRow({
  docId,
  label,
  required,
  why,
  hint,
  filename,
  onUpload,
  onRemove,
}: {
  docId: string;
  label: string;
  required: boolean;
  why: string;
  hint: string;
  filename?: string;
  onUpload: (docId: string, filename: string) => void;
  onRemove: (docId: string) => void;
}) {
  const done = Boolean(filename);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        border: "1px solid var(--ua-border)",
        borderLeft: `3px solid ${done ? "var(--ua-success)" : required ? "var(--ua-warning)" : "var(--ua-border)"}`,
        borderRadius: "6px",
        padding: "12px 14px",
        background: "var(--ua-surface)",
      }}
    >
      <span
        style={{
          width: "18px",
          height: "18px",
          borderRadius: "50%",
          background: done ? "var(--ua-success)" : "var(--ua-border)",
          color: done ? "#fff" : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "10px",
          fontWeight: 900,
          flexShrink: 0,
          marginTop: "2px",
        }}
      >
        {done ? "✓" : ""}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, fontSize: "14px", color: "var(--ua-navy)" }}>
            {label}
          </span>
          <span
            style={{
              fontSize: "10px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.03em",
              borderRadius: "999px",
              padding: "1px 8px",
              background: required ? "#fdf2e7" : "var(--ua-bg)",
              border: `1px solid ${required ? "#f0c79a" : "var(--ua-border)"}`,
              color: required ? "var(--ua-navy)" : "var(--ua-muted)",
            }}
          >
            {required ? "Verplicht" : "Optioneel"}
          </span>
        </div>
        <p
          style={{ margin: "4px 0 0", fontSize: "12px", color: "var(--ua-muted)", lineHeight: 1.5 }}
        >
          {why}
        </p>
        {done ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
              marginTop: "8px",
            }}
          >
            <span style={{ fontSize: "12px", fontWeight: 700, color: "var(--ua-navy)" }}>
              📄 {filename}
            </span>
            <button
              type="button"
              onClick={() => onRemove(docId)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                fontSize: "12px",
                fontWeight: 700,
                color: "var(--ua-blue)",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Verwijder
            </button>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
              marginTop: "8px",
            }}
          >
            <label
              style={{
                fontSize: "12px",
                fontWeight: 700,
                color: "var(--ua-blue)",
                background: "none",
                border: "1px solid #c5d0e8",
                borderRadius: "999px",
                padding: "3px 12px",
                cursor: "pointer",
              }}
            >
              Uploaden
              <input
                type="file"
                style={{ display: "none" }}
                onChange={(e) => {
                  const name = e.target.files?.[0]?.name;
                  if (name) {
                    onUpload(docId, name);
                  }
                  e.target.value = "";
                }}
              />
            </label>
            <span style={{ fontSize: "11px", color: "var(--ua-muted)" }}>{hint}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── AI analyzing state (between upload and wizard) ───────────────────────── */

function AnalyzingState({ docs }: { docs: { label: string; name: string }[] }) {
  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          border: "1px dashed var(--ua-border)",
          borderRadius: "8px",
          padding: "32px 24px",
          textAlign: "center",
          background: "linear-gradient(90deg, #fafbfc, #f2f5f9, #fafbfc)",
          backgroundSize: "200% 100%",
          animation: "intake-shimmer 1.4s ease-in-out infinite",
        }}
      >
        <AgentAvatar size={40} style={{ margin: "0 auto 14px" }} />
        <p style={{ fontWeight: 700, color: "var(--ua-navy)", margin: "0 0 4px" }}>
          Etna leest je documenten…
        </p>
        <p style={{ color: "var(--ua-muted)", fontSize: "13px", margin: "0 0 16px" }}>
          Etna vult de aanvraag alvast in op basis van wat je hebt toegevoegd.
        </p>
        <div style={{ display: "grid", gap: "6px", maxWidth: "360px", margin: "0 auto" }}>
          {docs.map((d) => (
            <div
              key={d.name}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "12px",
                color: "var(--ua-navy)",
                background: "var(--ua-white)",
                border: "1px solid var(--ua-border)",
                borderRadius: "6px",
                padding: "7px 11px",
                textAlign: "left",
              }}
            >
              <span>📄</span>
              <span style={{ fontWeight: 700, flex: 1, minWidth: 0 }}>{d.label}</span>
              <span style={{ color: "var(--ua-muted)" }}>lezen…</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Form field row ────────────────────────────────────────────────────────── */

function FieldRow({
  field,
  tips = [],
  highlighted = false,
  currentValue,
  toelichtingValue,
  confirmed,
  onFieldChange,
  onConfirm,
}: {
  field: IntakeField;
  tips?: Tip[];
  highlighted?: boolean;
  currentValue: string;
  toelichtingValue: string;
  confirmed: boolean;
  onFieldChange: (id: string, value: string) => void;
  onConfirm: (id: string) => void;
}) {
  // A radio answer that carries a pre-fill gets an editable toelichting; an Nvt
  // answer has no toelichting key, so nothing renders.
  const showToelichting = field.type === "radio" && field.toelichting?.[currentValue] !== undefined;
  return (
    <div
      id={`field-${field.id}`}
      style={{
        display: "grid",
        gap: "6px",
        scrollMarginTop: "16px",
        borderRadius: "var(--ua-radius)",
        padding: "10px",
        margin: "-10px",
        background: highlighted ? "var(--ua-bg)" : "transparent",
        boxShadow: highlighted ? "0 0 0 2px var(--ua-blue)" : "0 0 0 0 transparent",
        transition: "box-shadow 0.35s ease, background 0.35s ease",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap",
          fontSize: "13px",
          fontWeight: 700,
          color: "var(--ua-navy)",
        }}
      >
        <label htmlFor={field.id}>{field.label}</label>
        {field.aiPrefilled && <AiBadge confirmed={confirmed} />}
        {field.luik && (
          <span style={{ fontSize: "11px", fontWeight: 700, color: "var(--ua-muted)" }}>
            · {field.luik}
          </span>
        )}
      </span>

      {field.type === "textarea" ? (
        <textarea
          id={field.id}
          rows={3}
          value={currentValue}
          onChange={(e) => onFieldChange(field.id, e.target.value)}
          style={fieldStyle}
        />
      ) : field.type === "select" ? (
        <select
          id={field.id}
          value={currentValue}
          onChange={(e) => onFieldChange(field.id, e.target.value)}
          style={fieldStyle}
        >
          {(field.options ?? [field.value]).map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      ) : field.type === "radio" ? (
        <RadioGroup
          field={field}
          value={currentValue}
          onChange={(v) => onFieldChange(field.id, v)}
        />
      ) : (
        <input
          id={field.id}
          type="text"
          value={currentValue}
          onChange={(e) => onFieldChange(field.id, e.target.value)}
          style={fieldStyle}
        />
      )}

      {field.hint && (
        <span style={{ fontSize: "12px", color: "var(--ua-muted)", lineHeight: 1.5 }}>
          {field.hint}
        </span>
      )}

      {/* Editable, pre-filled toelichting for the selected radio answer */}
      {showToelichting && (
        <div style={{ display: "grid", gap: "4px", marginTop: "2px" }}>
          <span
            style={{
              fontSize: "11px",
              fontWeight: 700,
              color: "var(--ua-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.03em",
            }}
          >
            Toelichting
          </span>
          <textarea
            rows={3}
            value={toelichtingValue}
            placeholder="Licht je antwoord kort toe"
            onChange={(e) =>
              onFieldChange(`${field.id}::toelichting::${currentValue}`, e.target.value)
            }
            style={fieldStyle}
          />
        </div>
      )}

      {/* Per-field AI confirmation button */}
      {field.aiPrefilled && (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "2px" }}>
          {confirmed ? (
            <span style={{ fontSize: "12px", color: "var(--ua-success)", fontWeight: 700 }}>
              ✓ Bevestigd
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onConfirm(field.id)}
              style={{
                fontSize: "12px",
                fontWeight: 700,
                color: "var(--ua-blue)",
                background: "none",
                border: "1px solid #c5d0e8",
                borderRadius: "999px",
                padding: "3px 12px",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              ✓ Bevestig dit veld
            </button>
          )}
        </div>
      )}

      {/* Guide tips anchored to this field, rendered inline, attached to the input */}
      {tips.map((tip) => (
        <InlineFieldTip key={tip.id} tip={tip} value={currentValue} />
      ))}
    </div>
  );
}

function InlineFieldTip({ tip, value }: { tip: Tip; value: string }) {
  const state = resolveTip(tip, value);
  const t = tipVisuals(state.tone);
  return (
    <div
      style={{
        marginTop: "2px",
        border: `1px solid ${t.border}`,
        borderLeft: `3px solid ${t.accent}`,
        borderRadius: "6px",
        background: t.bg,
        padding: "10px 12px",
        transition: "background 0.3s ease, border-color 0.3s ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "7px", marginBottom: "4px" }}>
        <span
          aria-hidden
          style={{
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            background: t.accent,
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "10px",
            fontWeight: 900,
            flexShrink: 0,
          }}
        >
          {t.icon}
        </span>
        <span
          style={{
            fontSize: "10px",
            fontWeight: 700,
            color: t.accent,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {state.resolved ? `${t.label} · opgelost` : `${t.label} · van Etna`}
        </span>
      </div>
      <p
        style={{ margin: "0 0 4px", fontWeight: 700, fontSize: "12.5px", color: "var(--ua-navy)" }}
      >
        {state.title}
      </p>
      <p style={{ margin: "0 0 6px", fontSize: "12px", color: "var(--ua-muted)", lineHeight: 1.5 }}>
        {state.body}
      </p>
      <span
        style={{
          fontSize: "10.5px",
          fontWeight: 700,
          background: "var(--ua-surface)",
          border: `1px solid ${t.border}`,
          color: "var(--ua-navy)",
          borderRadius: "999px",
          padding: "1px 8px",
          display: "inline-block",
        }}
      >
        {tip.source}
      </span>
    </div>
  );
}

function RadioGroup({
  field,
  value,
  onChange,
}: {
  field: IntakeField;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
      {(field.options ?? []).map((o) => {
        const active = value === o;
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            style={{
              padding: "7px 16px",
              borderRadius: "999px",
              border: "1px solid",
              borderColor: active ? "var(--ua-navy)" : "var(--ua-border)",
              background: active ? "var(--ua-navy)" : "var(--ua-surface)",
              color: active ? "#fff" : "var(--ua-navy)",
              fontWeight: 700,
              fontSize: "13px",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

function AiBadge({ confirmed }: { confirmed?: boolean }) {
  return (
    <span
      style={{
        fontSize: "10px",
        fontWeight: 700,
        background: confirmed ? "#edfaf2" : "#eef2f9",
        border: `1px solid ${confirmed ? "#a3e4c1" : "#c5d0e8"}`,
        color: confirmed ? "var(--ua-success)" : "var(--ua-navy)",
        borderRadius: "999px",
        padding: "1px 8px",
        textTransform: "uppercase",
        letterSpacing: "0.03em",
      }}
    >
      {confirmed ? "✓ Bevestigd" : "✦ AI ingevuld"}
    </span>
  );
}

/* ── Inline progress toward "all AI fields confirmed" on this step ───────── */

function AiConfirmProgress({ confirmed, total }: { confirmed: number; total: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span style={{ fontSize: "12px", color: "var(--ua-muted)" }}>
        Bevestig eerst alle AI-velden op deze stap.
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            title={i < confirmed ? "Bevestigd" : "Nog te bevestigen"}
            style={{
              width: "9px",
              height: "9px",
              borderRadius: "50%",
              background: i < confirmed ? "var(--ua-success)" : "var(--ua-border)",
              transition: "background 0.2s ease, transform 0.2s ease",
              transform: i === confirmed - 1 ? "scale(1.15)" : "scale(1)",
            }}
          />
        ))}
      </div>
      <span style={{ fontSize: "12px", fontWeight: 700, color: "var(--ua-navy)" }}>
        {confirmed}/{total}
      </span>
    </div>
  );
}

const fieldStyle: React.CSSProperties = {
  fontFamily: "inherit",
  fontSize: "14px",
  color: "var(--ua-navy)",
  background: "var(--ua-white)",
  border: "1px solid var(--ua-border)",
  borderRadius: "var(--ua-radius)",
  padding: "9px 12px",
  width: "100%",
};

/* ── Final review / voorafcheck step ──────────────────────────────────────── */

function ReviewStep({
  checking,
  checkDone,
  onJump,
  documents,
  fieldValues,
  confirmedCount,
  totalAiFields,
  docsUploaded,
  signature,
  onSign,
  onUnsign,
}: {
  checking: boolean;
  checkDone: boolean;
  onJump: (i: number) => void;
  documents: { label: string; name: string; required: boolean }[];
  fieldValues: Record<string, string>;
  confirmedCount: number;
  totalAiFields: number;
  docsUploaded: { methodologie: boolean; consent: boolean };
  signature: { name: string; signedAt: string } | null;
  onSign: (name: string) => void;
  onUnsign: () => void;
}) {
  return (
    <div style={{ display: "grid", gap: "28px" }}>
      {/* 1 · Readiness overview: the checklist, as the header of the review */}
      <ReadinessOverview
        docsUploaded={docsUploaded}
        confirmedCount={confirmedCount}
        totalAiFields={totalAiFields}
        checkDone={checkDone}
      />

      {/* 2 · What you're submitting: generated overview + uploaded attachments */}
      <div>
        <p className="section-kicker">Wat je indient</p>
        <div style={{ display: "grid", gap: "12px", marginTop: "8px" }}>
          <GeneratedSummaryCard fieldValues={fieldValues} onJump={onJump} />
          <AttachmentsList documents={documents} />
        </div>
      </div>

      {/* 3 · Voorafcheck */}
      <div>
        <p className="section-kicker">Voorafcheck van Etna</p>
        {checking ? (
          <div
            style={{
              border: "1px dashed var(--ua-border)",
              borderRadius: "6px",
              padding: "24px",
              textAlign: "center",
              color: "var(--ua-muted)",
              fontSize: "13px",
              background: "linear-gradient(90deg, #fafbfc, #f2f5f9, #fafbfc)",
              backgroundSize: "200% 100%",
              animation: "intake-shimmer 1.4s ease-in-out infinite",
              marginTop: "8px",
            }}
          >
            Etna controleert je volledige dossier op onduidelijkheden…
          </div>
        ) : (
          checkDone &&
          (() => {
            const resolvedCount = PRESCREEN_FLAGS.filter((flag) =>
              isFlagResolved(flag, fieldValues),
            ).length;
            const openCount = PRESCREEN_FLAGS.length - resolvedCount;
            return (
              <>
                <div className="info-banner" style={{ marginTop: "8px", marginBottom: "14px" }}>
                  <span className="info-banner__icon">ℹ</span>
                  {openCount === 0 ? (
                    <span>
                      Alle <strong>{PRESCREEN_FLAGS.length} aandachtspunten</strong> zijn opgelost.
                      Je kan indienen, het secretariaat beslist altijd.
                    </span>
                  ) : (
                    <span>
                      Etna vond <strong>{PRESCREEN_FLAGS.length} aandachtspunten</strong>
                      {resolvedCount > 0 && (
                        <>
                          {": "}
                          <strong>{resolvedCount} opgelost</strong>, {openCount} open
                        </>
                      )}
                      . Deze check houdt je aanvraag niet tegen. Pas aan, licht kort toe, of laat
                      het secretariaat meekijken. Het secretariaat beslist altijd.
                    </span>
                  )}
                </div>
                <div style={{ display: "grid", gap: "10px" }}>
                  {PRESCREEN_FLAGS.map((flag) => (
                    <FlagRow
                      key={flag.id}
                      flag={flag}
                      resolved={isFlagResolved(flag, fieldValues)}
                      onJump={onJump}
                    />
                  ))}
                </div>
              </>
            );
          })()
        )}
      </div>

      {/* 4 · Declaration */}
      {checkDone && (
        <div>
          <p className="section-kicker">Verklaring</p>
          <div
            style={{
              border: "1px solid var(--ua-border)",
              borderRadius: "6px",
              padding: "14px 16px",
              background: "var(--ua-bg)",
              fontSize: "13px",
              color: "var(--ua-muted)",
              lineHeight: 1.6,
              marginTop: "8px",
            }}
          >
            <strong style={{ color: "var(--ua-navy)" }}>Verklaring (Luik E):</strong> Ik bevestig
            dat ik dit document in eer en geweten invul, dat ik het onderzoek monitor, en dat ik pas
            start na een (voorlopig) positief ethisch advies.
          </div>
          <SignatureBlock signature={signature} onSign={onSign} onUnsign={onUnsign} />
        </div>
      )}
    </div>
  );
}

/* ── Digital signature (mock) ───────────────────────────────────────────── */

function SignatureBlock({
  signature,
  onSign,
  onUnsign,
}: {
  signature: { name: string; signedAt: string } | null;
  onSign: (name: string) => void;
  onUnsign: () => void;
}) {
  const [name, setName] = useState("");

  if (signature) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          border: "1px solid var(--ua-success, #a3e4c1)",
          borderRadius: "6px",
          padding: "12px 16px",
          background: "#edfaf2",
          marginTop: "10px",
        }}
      >
        <div style={{ fontSize: "13px", color: "var(--ua-navy)", lineHeight: 1.5 }}>
          <div style={{ fontWeight: 700 }}>✓ Digitaal ondertekend</div>
          <div style={{ fontFamily: "cursive", fontSize: "20px", margin: "4px 0" }}>
            {signature.name}
          </div>
          <div style={{ color: "var(--ua-muted)", fontSize: "12px" }}>
            Getekend op {signature.signedAt}
          </div>
        </div>
        <button className="btn btn--ghost" onClick={onUnsign}>
          Handtekening intrekken
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        marginTop: "10px",
        flexWrap: "wrap",
      }}
    >
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Typ je volledige naam als handtekening"
        style={{
          flex: "1 1 260px",
          padding: "8px 12px",
          border: "1px solid var(--ua-border)",
          borderRadius: "6px",
          fontSize: "13px",
        }}
      />
      <button
        className="btn btn--primary"
        disabled={name.trim().length < 2}
        onClick={() => onSign(name.trim())}
      >
        Onderteken verklaring
      </button>
    </div>
  );
}

/* ── Readiness overview (submission checklist, as the review header) ──────── */

function ReadinessOverview({
  docsUploaded,
  confirmedCount,
  totalAiFields,
  checkDone,
}: {
  docsUploaded: { methodologie: boolean; consent: boolean };
  confirmedCount: number;
  totalAiFields: number;
  checkDone: boolean;
}) {
  const items = [
    {
      label: "Methodologie-bijlage geüpload",
      done: docsUploaded.methodologie,
      why: "Verplichte bijlage (Luik D).",
    },
    {
      label: "Informed consent-bijlage geüpload",
      done: docsUploaded.consent,
      why: "Verplichte bijlage (Luik D).",
    },
    {
      label: `AI-velden bevestigd (${confirmedCount}/${totalAiFields})`,
      done: totalAiFields > 0 && confirmedCount >= totalAiFields,
      why: "Elke AI-suggestie apart bevestigd.",
    },
    {
      label: "Risicoanalyse ingevuld",
      done: true,
      why: "Luik C: alle stappen doorlopen.",
    },
    {
      label: "Voorafcheck uitgevoerd",
      done: checkDone,
      why: "Etna scant je dossier.",
    },
  ];
  const doneCount = items.filter((i) => i.done).length;
  const allDone = doneCount === items.length;

  return (
    <div
      style={{
        border: `1px solid ${allDone ? "#a3e4c1" : "var(--ua-border)"}`,
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "14px 16px",
          background: allDone ? "#edfaf2" : "var(--ua-bg)",
          borderBottom: "1px solid var(--ua-border)",
        }}
      >
        <span
          style={{
            width: "30px",
            height: "30px",
            borderRadius: "50%",
            background: allDone ? "var(--ua-success)" : "var(--ua-navy)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "15px",
            fontWeight: 900,
            flexShrink: 0,
          }}
        >
          {allDone ? "✓" : doneCount}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "var(--ua-navy)" }}>
            {allDone ? "Je dossier is klaar om in te dienen" : "Bijna klaar om in te dienen"}
          </p>
          <p style={{ margin: 0, fontSize: "12px", color: "var(--ua-muted)" }}>
            {doneCount} van {items.length} punten in orde
            {allDone ? "" : " – rond de resterende punten hieronder af."}
          </p>
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
          gap: "10px 20px",
          padding: "14px 16px",
          background: "var(--ua-surface)",
        }}
      >
        {items.map((item) => (
          <div key={item.label} style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
            <span
              style={{
                width: "16px",
                height: "16px",
                borderRadius: "50%",
                background: item.done ? "var(--ua-success)" : "var(--ua-border)",
                color: item.done ? "#fff" : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "9px",
                fontWeight: 900,
                flexShrink: 0,
                marginTop: "2px",
              }}
            >
              {item.done ? "✓" : ""}
            </span>
            <span style={{ minWidth: 0 }}>
              <span
                style={{
                  display: "block",
                  fontSize: "12px",
                  color: item.done ? "var(--ua-navy)" : "var(--ua-muted)",
                  fontWeight: item.done ? 700 : 400,
                }}
              >
                {item.label}
              </span>
              <span
                style={{
                  display: "block",
                  fontSize: "11px",
                  color: "var(--ua-muted)",
                  lineHeight: 1.4,
                }}
              >
                {item.why}
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Generated dossier overview (auto-built from the intake answers) ──────── */

function summaryFields(step: IntakeStep, fieldValues: Record<string, string>) {
  return step.fields.filter((f) => !f.showIf || fieldValues[f.showIf.fieldId] === f.showIf.value);
}

function buildSummaryMarkdown(fieldValues: Record<string, string>) {
  const lines: string[] = [
    `# Dossieroverzicht: ${APPLICATION_REF}`,
    "",
    "_Automatisch gegenereerd uit je intake-antwoorden. Fictieve voorbeelddata._",
    "",
  ];
  for (const step of STEPS.slice(0, -1)) {
    lines.push(`## ${step.title}`, `_${step.luik}_`, "");
    for (const f of summaryFields(step, fieldValues)) {
      lines.push(`- **${f.label}:** ${fieldValues[f.id] ?? f.value ?? "(niet ingevuld)"}`);
    }
    lines.push("");
  }
  return lines.join("\n");
}

function downloadSummary(fieldValues: Record<string, string>) {
  const blob = new Blob([buildSummaryMarkdown(fieldValues)], {
    type: "text/markdown;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Dossieroverzicht_${APPLICATION_REF}.md`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function GeneratedSummaryCard({
  fieldValues,
  onJump,
}: {
  fieldValues: Record<string, string>;
  onJump: (i: number) => void;
}) {
  const [open, setOpen] = useState(true);
  const formSteps = STEPS.slice(0, -1);
  const fieldCount = formSteps.reduce((sum, s) => sum + summaryFields(s, fieldValues).length, 0);

  return (
    <div
      style={{
        border: "1px solid var(--ua-border)",
        borderRadius: "6px",
        overflow: "hidden",
        background: "var(--ua-white)",
      }}
    >
      {/* File header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "12px 14px",
          background: "var(--ua-surface)",
          borderBottom: open ? "1px solid var(--ua-border)" : "none",
        }}
      >
        <span
          style={{
            width: "34px",
            height: "34px",
            borderRadius: "6px",
            background: "#eef2f9",
            border: "1px solid #c5d0e8",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "16px",
            flexShrink: 0,
          }}
        >
          📄
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: "14px", color: "var(--ua-navy)" }}>
            Dossieroverzicht_{APPLICATION_REF}
            <span
              style={{
                marginLeft: "8px",
                fontSize: "10px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                background: "#eef2f9",
                border: "1px solid #c5d0e8",
                color: "var(--ua-navy)",
                borderRadius: "999px",
                padding: "1px 8px",
                whiteSpace: "nowrap",
              }}
            >
              ✦ Gegenereerd
            </span>
          </p>
          <p style={{ margin: 0, fontSize: "12px", color: "var(--ua-muted)" }}>
            Samenvatting van {fieldCount} ingevulde velden uit je aanvraag
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            style={{
              fontSize: "12px",
              fontWeight: 700,
              color: "var(--ua-blue)",
              background: "none",
              border: "1px solid #c5d0e8",
              borderRadius: "999px",
              padding: "4px 12px",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {open ? "Verberg" : "Bekijk"}
          </button>
          <button
            type="button"
            onClick={() => downloadSummary(fieldValues)}
            className="btn btn--ghost"
            style={{ fontSize: "12px", padding: "5px 12px" }}
          >
            ↓ Download
          </button>
        </div>
      </div>

      {/* Inline preview of the answers, grouped by step */}
      {open && (
        <div style={{ padding: "6px 14px 14px" }}>
          {formSteps.map((step, i) => (
            <div
              key={step.id}
              style={{
                paddingTop: "12px",
                borderTop: i === 0 ? "none" : "1px solid var(--ua-border)",
                marginTop: i === 0 ? 0 : "12px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  gap: "10px",
                  marginBottom: "8px",
                }}
              >
                <span style={{ fontWeight: 700, fontSize: "13px", color: "var(--ua-navy)" }}>
                  {step.title}
                  <span style={{ marginLeft: "8px", fontSize: "11px", color: "var(--ua-muted)" }}>
                    {step.luik}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => onJump(i)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "var(--ua-blue)",
                    cursor: "pointer",
                    textDecoration: "underline",
                    whiteSpace: "nowrap",
                    fontFamily: "inherit",
                  }}
                >
                  Bewerk
                </button>
              </div>
              <dl style={{ display: "grid", gap: "6px", margin: 0 }}>
                {summaryFields(step, fieldValues).map((f) => (
                  <div
                    key={f.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(140px, 200px) 1fr",
                      gap: "12px",
                      alignItems: "baseline",
                    }}
                  >
                    <dt style={{ fontSize: "12px", color: "var(--ua-muted)" }}>{f.label}</dt>
                    <dd
                      style={{
                        margin: 0,
                        fontSize: "13px",
                        color: "var(--ua-navy)",
                        lineHeight: 1.5,
                      }}
                    >
                      {fieldValues[f.id] || f.value || "(niet ingevuld)"}
                      {fieldValues[`${f.id}::toelichting::${fieldValues[f.id] ?? f.value}`] && (
                        <span
                          style={{
                            display: "block",
                            marginTop: "2px",
                            fontSize: "12px",
                            color: "var(--ua-muted)",
                            lineHeight: 1.45,
                          }}
                        >
                          {fieldValues[`${f.id}::toelichting::${fieldValues[f.id] ?? f.value}`]}
                        </span>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Uploaded attachments list ────────────────────────────────────────────── */

function AttachmentsList({
  documents,
}: {
  documents: { label: string; name: string; required: boolean }[];
}) {
  return (
    <div style={{ display: "grid", gap: "10px" }}>
      {documents.map((a) => (
        <div
          key={a.name}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            border: "1px solid var(--ua-border)",
            borderRadius: "6px",
            padding: "12px 14px",
            background: "var(--ua-white)",
          }}
        >
          <span
            style={{
              width: "34px",
              height: "34px",
              borderRadius: "6px",
              background: "#edfaf2",
              border: "1px solid #a3e4c1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "16px",
              flexShrink: 0,
            }}
          >
            📎
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontWeight: 700,
                fontSize: "14px",
                color: "var(--ua-navy)",
              }}
            >
              {a.label}
              <span
                style={{
                  marginLeft: "8px",
                  fontSize: "10px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                  color: a.required ? "var(--ua-navy)" : "var(--ua-muted)",
                }}
              >
                {a.required ? "verplicht" : "aanvullend"}
              </span>
            </p>
            <p style={{ margin: 0, fontSize: "12px", color: "var(--ua-muted)" }}>{a.name}</p>
          </div>
          <span style={{ fontSize: "16px", color: "var(--ua-success)", flexShrink: 0 }}>✓</span>
        </div>
      ))}
    </div>
  );
}

/** A flag is resolved once its linked field holds the resolving answer. */
function isFlagResolved(flag: PrescreenFlag, fieldValues: Record<string, string>): boolean {
  return Boolean(flag.resolve && fieldValues[flag.resolve.fieldId] === flag.resolve.resolvedValue);
}

function FlagRow({
  flag,
  resolved,
  onJump,
}: {
  flag: PrescreenFlag;
  resolved: boolean;
  onJump: (i: number) => void;
}) {
  const color = resolved
    ? "var(--ua-success)"
    : flag.severity === "high"
      ? "var(--ua-red)"
      : flag.severity === "medium"
        ? "var(--ua-warning)"
        : "var(--ua-muted)";
  const sevLabel = resolved
    ? "Opgelost"
    : flag.severity === "high"
      ? "Belangrijk"
      : flag.severity === "medium"
        ? "Aanvullen"
        : "Ter info";
  const title = resolved && flag.resolve ? flag.resolve.resolvedTitle : flag.title;
  const description =
    resolved && flag.resolve ? flag.resolve.resolvedDescription : flag.description;

  return (
    <div
      style={{
        border: "1px solid var(--ua-border)",
        borderLeft: `4px solid ${color}`,
        borderRadius: "6px",
        padding: "14px 16px",
        background: resolved ? "#f4fbf7" : "var(--ua-surface)",
        transition: "background 0.3s ease, border-color 0.3s ease",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
          marginBottom: "6px",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "11px",
            fontWeight: 700,
            color,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {resolved && (
            <span
              aria-hidden
              style={{
                width: "15px",
                height: "15px",
                borderRadius: "50%",
                background: "var(--ua-success)",
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "9px",
                fontWeight: 900,
              }}
            >
              ✓
            </span>
          )}
          {sevLabel} · {flag.category}
        </span>
        <button
          type="button"
          onClick={() => onJump(flag.stepIndex)}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            fontSize: "12px",
            fontWeight: 700,
            color: "var(--ua-blue)",
            cursor: "pointer",
            textDecoration: "underline",
            whiteSpace: "nowrap",
          }}
        >
          {resolved ? "Bekijk stap →" : "Naar stap →"}
        </button>
      </div>
      <p
        style={{
          margin: "0 0 4px",
          fontWeight: 700,
          fontSize: "14px",
          color: "var(--ua-navy)",
        }}
      >
        {title}
      </p>
      <p
        style={{ margin: "0 0 8px", fontSize: "13px", color: "var(--ua-muted)", lineHeight: 1.55 }}
      >
        {description}
      </p>
      <span
        style={{
          fontSize: "11px",
          fontWeight: 700,
          background: "#eef2f9",
          border: "1px solid #c5d0e8",
          color: "var(--ua-navy)",
          borderRadius: "999px",
          padding: "2px 9px",
          display: "inline-block",
        }}
      >
        {flag.source}
      </span>
    </div>
  );
}

const INTAKE_KEYFRAMES = `
  @keyframes intake-pop {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes intake-shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;
